import { FileText } from "lucide-react";
import ContentBlocks from "../components/content/ContentBlocks";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import { ButtonLink } from "../components/ui/Button";
import SectionHeading from "../components/ui/SectionHeading";
import { pageAt } from "../content";
import { images } from "../data/images";
import { routes } from "../data/site";
import { photoProps } from "../lib/links";
import EbookForm from "./EbookForm";

// The e-book's own table of contents (the PDF is image-only, 16 pages).
const ebook = {
  title: "Construction companies' journey of *digital transformation.*",
  lede: "Observations from construction companies that grew from $100 million to $500 million and beyond — and the technology decisions that got them there.",
  meta: "Free PDF · 16 pages",
  chapters: [
    { title: "Growth doesn't come without challenges", page: 3 },
    { title: "Growing with intentionality", page: 4 },
    { title: "Helping technology leaders make the jump", page: 5 },
    { title: "What do the stats say", page: 6 },
    { title: "A step-by-step guide to success", page: 7 },
    { title: "A closer look at requirements", page: 9 },
    { title: "Conclusion", page: 16 },
  ],
  extra:
    "Plus an architectural survey checklist to take into your next software conversation.",
};

/** The original privacy policy captured on the e-book page, kept verbatim. */
function privacyPolicy() {
  const page = pageAt(routes.ebook);
  const start = page.body.findIndex((b) => b.text === "Privacy Policy");
  return start >= 0
    ? { blocks: page.body.slice(start), title: page.heading || page.title }
    : null;
}

function GetButton({ className }) {
  return (
    <ButtonLink href="#get-ebook" className={className}>
      Get the free e-book ↓
    </ButtonLink>
  );
}

export default function ResourcePage() {
  const policy = privacyPolicy();
  return (
    <>
      <PageHero eyebrow="Free e-book" title={ebook.title} lede={ebook.lede}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <GetButton />
          <span className="flex items-center gap-2 text-[15px] text-white/70">
            <FileText size={17} aria-hidden="true" />
            {ebook.meta}
          </span>
        </div>
      </PageHero>

      <section className="bg-white px-4 py-20 sm:px-6 md:py-24">
        <div className="mx-auto grid max-w-280 grid-cols-1 items-center gap-14 md:grid-cols-[minmax(0,300px)_minmax(0,1fr)] lg:grid-cols-[380px_minmax(0,1fr)] lg:gap-20">
          <div className="relative mx-auto w-full max-w-75 md:max-w-none">
            <div
              aria-hidden="true"
              className="blueprint absolute -inset-4 -rotate-3 rounded-brand"
            />
            <img
              {...photoProps(
                images.ebookCover,
                "(max-width: 768px) 300px, 380px",
              )}
              alt="Cover of the e-book Construction Companies Journey of Digital Transformation"
              className="relative w-full rounded-brand bg-white shadow-lift"
            />
          </div>
          <div>
            <SectionHeading
              eyebrow="What's inside"
              title="Seven chapters. *One roadmap.*"
            />
            <ol className="mt-8 border-t border-line">
              {ebook.chapters.map(({ title, page }, i) => (
                <li
                  key={title}
                  className="flex items-baseline gap-5 border-b border-line py-3.5"
                >
                  <span className="font-display w-7 shrink-0 text-xl text-yellow">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 text-[17px] font-bold text-slate">
                    {title}
                  </span>
                  <span className="text-sm text-muted">p. {page}</span>
                </li>
              ))}
            </ol>
            <p className="mt-5 text-muted">{ebook.extra}</p>
            <GetButton className="mt-8" />
          </div>
        </div>
      </section>

      <section
        id="get-ebook"
        className="bg-surface px-4 pt-20 pb-6 sm:px-6 md:pt-24"
      >
        <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] lg:gap-16">
          <SectionHeading eyebrow="Free download" title="Get your *copy.*">
            <p>
              Tell us who you are and the PDF is yours. We'll only use your
              details as described in the privacy policy below.
            </p>
          </SectionHeading>
          <div className="rounded-brand border border-line bg-white p-6 shadow-soft sm:p-8">
            <EbookForm />
          </div>
        </div>
      </section>

      {policy && (
        <section className="bg-surface px-4 py-16 sm:px-6 md:py-20">
          <div className="mx-auto max-w-190 rounded-brand border border-line bg-white p-6 text-[15px] sm:p-10">
            <ContentBlocks blocks={policy.blocks} title={policy.title} />
          </div>
        </section>
      )}
      <CtaPanel className="bg-white" />
    </>
  );
}
